import { fetch } from "@self/fetch";
import { noticeFailure } from "@self/notices";
import { Result } from "@self/Result";
import { ObservedAttributes } from "@self/utils";
import { bound, CustomElement, customElement } from "./CustomElement";

@customElement("gct-quantity")
export class Quantity extends CustomElement {

	static get observedAttributes() {
		return ["min", "max", "value", "disabled"] as const;
	}

	input: HTMLInputElement;
	increment: HTMLButtonElement;
	decrement: HTMLButtonElement;

	get value() { return Number(this.getAttribute("value")); }
	set value(value: number) { this.setAttribute("value", String(value)); }

	get min() {
		const attr = this.getAttribute("min");
		return attr !== null && attr !== "" ? Number(attr) : null;
	}

	set min(value: number | null) {
		if (value !== null) {
			this.setAttribute("min", String(value));
		} else {
			this.removeAttribute("min");
		}
	}

	get max() {
		const attr = this.getAttribute("max");
		return attr !== null && attr !== "" ? Number(attr) : null;
	}

	set max(value: number | null) {
		if (value !== null) {
			this.setAttribute("max", String(value));
		} else {
			this.removeAttribute("max");
		}
	}

	get disabled() { return this.hasAttribute("disabled"); }

	set disabled(value: boolean) {
		if (value) {
			this.setAttribute("disabled", "");
		} else {
			this.removeAttribute("disabled");
		}
	}

	constructor() {
		const input = <input
			type="number"
			className="gct-quantity-input"
			step="1"
		/> as HTMLInputElement;

		const increment = <button
			type="button"
			className="gct-cart-btn"
			innerHTML={"<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"20\" viewBox=\"0 0 20 20\" fill=\"none\"><path d=\"M9.25 10.75H5V9.25H9.25V5H10.75V9.25H15V10.75H10.75V15H9.25V10.75Z\" fill=\"#747474\"/></svg>"}
		/> as HTMLButtonElement;

		const decrement = <button
			type="button"
			className="gct-cart-btn"
			innerHTML={"<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"20\" viewBox=\"0 0 20 20\" fill=\"none\"><path d=\"M4.8335 10.75V9.25H15.1668V10.75H4.8335Z\" fill=\"#747474\"/></svg>"}
		/> as HTMLButtonElement;

		super(decrement, input, increment);

		const min = this.getAttribute("min");
		if (min !== null) input.min = min;

		const max = this.getAttribute("max");
		if (max !== null) input.max = max;

		const value = this.getAttribute("value");
		if (value !== null) input.value = value;

		const disabled = this.getAttribute("disabled");
		if (disabled !== null) {
			input.disabled = true;
			increment.disabled = true;
			decrement.disabled = true;
		}

		this.input = input;
		this.increment = increment;
		this.decrement = decrement;
	}

	connectedCallback() {
		this.input.addEventListener("change", this.onChange);
		this.decrement.addEventListener("click", this.onDecrement);
		this.increment.addEventListener("click", this.onIncrement);
	}

	disconnectedCallback() {
		this.input.removeEventListener("change", this.onChange);
		this.decrement.removeEventListener("click", this.onDecrement);
		this.increment.removeEventListener("click", this.onIncrement);
	}

	attributeChangedCallback(name: ObservedAttributes<typeof Quantity>, _oldValue: string | null, newValue: string | null) {
		switch (name) {
			case "min":
				this.input.min = newValue ?? "";
				break;
			case "max":
				this.input.max = newValue ?? "";
				break;
			case "value":
				this.input.value = newValue ?? "";
				break;
		}

		const quantity = this.value;
		const min = this.min ?? -Infinity;
		const max = this.max ?? Infinity;

		if (name === "disabled" && newValue !== null) {
			this.input.disabled = true;
			this.decrement.disabled = true;
			this.increment.disabled = true;
		} else {
			this.input.disabled = false;
			this.decrement.disabled = quantity <= min;
			this.increment.disabled = quantity >= max;
		}
	}

	async postQuantityChange(quantity: number) {
		try {
			const {
				cartItemKey,
				endpoint,
				nonce,
			} = this.dataset;

			if (cartItemKey === undefined || endpoint === undefined || nonce === undefined) {
				this.value = quantity;
				return;
			}

			this.disabled = true;

			const data = new URLSearchParams({
				[`cart[${cartItemKey}][qty]`]: String(quantity),
				update_cart: "Update Cart",
				"woocommerce-cart-nonce": nonce,
			});

			const html = await Result.flatMapAsync(
				fetch(endpoint, {
					method: "POST",
					body: data,
					credentials: "include",
				}),
				(res) => res.text(),
			);

			html.match({
				onSuccess: (html) => {
					const newDocument = new DOMParser().parseFromString(html, "text/html");

					document.body.innerHTML = newDocument.body.innerHTML;
					this.value = quantity;
				},
				onFailure: (error) => {
					console.error(error);
					noticeFailure("Couldn't connect. Are you online?");
				},
			});
		} catch (error) {
			noticeFailure("There was an unexpected error.");
		} finally {
			this.disabled = false;
		}
	}

	@bound
	async onChange() {
		const quantity = Number.parseInt(this.input.value, 10);
		if (!Number.isNaN(quantity) && quantity >= 0) {
			await this.postQuantityChange(quantity);
		}
	}

	@bound
	async onDecrement() {
		await this.postQuantityChange(this.value - 1);
	}

	@bound
	async onIncrement() {
		await this.postQuantityChange(this.value + 1);
	}
}
